import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Steps,
  Typography,
} from "antd";
import {
  getOcppConfigurationQueryKey,
  getOcppActiveSocketsForSiteOptions,
  createOcppConfigurationMutation,
  updateOcppConfigurationMutation,
  deleteOcppConfigurationMutation,
} from "../../../services/client/@tanstack/react-query.gen";
import { getOcppConfiguration } from "../../../services/client/sdk.gen";
import type {
  ForwardUrlOverride,
  OcppBrokerMode,
} from "../../../services/client/types.gen";
import { useNotify } from "../../util-components/Notifications/notifications";
import { getOcppBaseUrl } from "../../../util/getOcppBaseUrl";
import { SocketToAssetMap } from "./SocketToAssetMap";
import { ForwardUrlOverridesField } from "./ForwardUrlOverridesField";
import { useIsViewer } from "../../../hooks/useIsViewer";
import { DisableForViewer } from "../../common/DisableForViewer";

const OCPP_BROKER_DOCS_URL =
  "https://api-docs.pleevi.ai/tutorial/integrations/ocpp-broker/portal-setup";

type OcppForwardingTabProps = {
  site_id: string;
  charging_pole_names: string[];
};

export const OcppForwardingTab: React.FC<OcppForwardingTabProps> = ({
  site_id,
  charging_pole_names,
}) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const isViewer = useIsViewer();

  const queryKey = getOcppConfigurationQueryKey({ path: { site_id } });

  const {
    data: config,
    isLoading,
    isError,
  } = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const { data, response } = await getOcppConfiguration({
        path: { site_id },
        signal,
      });
      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        throw new Error(
          `Failed to fetch OCPP configuration: ${response.status}`,
        );
      }
      return data;
    },
  });

  const configExists = !!config;

  // Polled so chargers show up, and step 3 completes, as soon as they connect.
  // The Socket-to-Asset Map reads the same cached query.
  const { data: activeSockets } = useQuery({
    ...getOcppActiveSocketsForSiteOptions({ path: { site_id } }),
    enabled: configExists,
    refetchInterval: 10_000,
  });
  const chargersConnected = (activeSockets?.length ?? 0) > 0;
  const currentStep = !configExists ? 0 : chargersConnected ? 3 : 1;

  const createMutation = useMutation({
    ...createOcppConfigurationMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      notify({
        type: "success",
        message: "OCPP Broker Created",
        description: "The broker configuration has been created.",
      });
    },
    onError: () => {
      notify({
        type: "error",
        message: "Failed to create OCPP broker configuration",
      });
    },
  });

  const updateMutation = useMutation({
    ...updateOcppConfigurationMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      notify({
        type: "success",
        message: "OCPP Broker Updated",
        description: "The broker configuration has been updated.",
      });
    },
    onError: () => {
      notify({
        type: "error",
        message: "Failed to update OCPP broker configuration",
      });
    },
  });

  const deleteMutation = useMutation({
    ...deleteOcppConfigurationMutation(),
    onSuccess: () => {
      form.resetFields();
      queryClient.invalidateQueries({ queryKey });
      notify({
        type: "success",
        message: "OCPP Broker Deleted",
        description: "The broker configuration has been deleted.",
      });
    },
    onError: () => {
      notify({
        type: "error",
        message: "Failed to delete OCPP broker configuration",
      });
    },
  });

  const onFinish = (values: {
    forward_url: string;
    mode: OcppBrokerMode;
    forward_url_overrides?: ForwardUrlOverride[];
  }) => {
    const forward_url_overrides = values.forward_url_overrides ?? [];
    if (configExists && config._id) {
      updateMutation.mutate({
        path: { configuration_id: config._id },
        body: {
          forward_url: values.forward_url,
          mode: values.mode,
          forward_url_overrides,
        },
      });
    } else {
      createMutation.mutate({
        body: {
          site_id,
          forward_url: values.forward_url,
          mode: values.mode,
          forward_url_overrides,
        },
      });
    }
  };

  const handleDelete = () => {
    if (config?._id) {
      deleteMutation.mutate({
        path: { configuration_id: config._id },
      });
    }
  };

  if (isLoading) {
    return null;
  }

  if (isError) {
    return (
      <Alert
        type="error"
        message="Failed to load OCPP broker configuration"
        showIcon
      />
    );
  }

  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  return (
    <div className="w-full xl:w-2/3">
      <Steps
        direction="vertical"
        size="small"
        current={currentStep}
        className="mb-6"
        items={[
          {
            title: "Enter your CPMS URL",
            description:
              "Fill in the WebSocket URL your chargers connect to today and click Create. All OCPP messages will be forwarded to this CPMS.",
          },
          {
            title: "Point your chargers to the Pleevi OCPP broker",
            description: (
              <>
                Replace the CPMS URL in the OCPP settings of each charger with:
                <div className="my-2">
                  <Typography.Text code copyable strong>
                    {`${getOcppBaseUrl()}/ocpp/${site_id}`}
                  </Typography.Text>
                </div>
                Keep the charge point ID and credentials as they are. The
                charger appends its ID to this URL.
              </>
            ),
          },
          {
            title: "Check that your chargers appear",
            description: (
              <>
                Connected chargers show up as sockets in the Socket-to-Asset Map
                below. Not showing up? See the{" "}
                <a
                  href={`${OCPP_BROKER_DOCS_URL}#troubleshooting`}
                  target="_blank"
                  rel="noreferrer"
                >
                  troubleshooting guide
                </a>
                .
              </>
            ),
          },
        ]}
      />
      <Form
        form={form}
        layout="vertical"
        disabled={isViewer}
        onFinish={onFinish}
        initialValues={{
          forward_url: config?.forward_url,
          mode: config?.mode ?? "transaction_linking",
          forward_url_overrides: config?.forward_url_overrides ?? [],
        }}
        className="bg-gray-50 p-4 rounded-md border-gray-100 border border-solid"
      >
        <Row gutter={16}>
          <Col span={16}>
            <Form.Item
              name="forward_url"
              label="CPMS URL"
              rules={[
                { required: true, message: "Please enter a CPMS URL" },
                { type: "url", message: "Please enter a valid URL" },
              ]}
            >
              <Input placeholder="wss://example.com/ocpp" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="mode"
              label="Mode"
              rules={[{ required: true, message: "Please select a mode" }]}
            >
              <Select
                options={[
                  {
                    value: "transaction_linking",
                    label: "Transaction linking",
                  },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
        <ForwardUrlOverridesField
          site_id={site_id}
          configured_socket_ids={config?.socket_to_asset_map}
        />
        <Space className="mt-4">
          <Button type="primary" htmlType="submit" loading={isPending}>
            {configExists ? "Update" : "Create"}
          </Button>
          {configExists && (
            <Popconfirm
              title="Delete OCPP broker configuration?"
              description="This action cannot be undone."
              onConfirm={handleDelete}
              okText="Delete"
              okType="danger"
            >
              <DisableForViewer>
                <Button danger loading={isPending}>
                  Delete
                </Button>
              </DisableForViewer>
            </Popconfirm>
          )}
        </Space>
      </Form>
      {configExists && config?._id && (
        <SocketToAssetMap
          site_id={site_id}
          ocpp_configuration={config}
          charging_pole_names={charging_pole_names}
        />
      )}
    </div>
  );
};
